import React from "react";
import {
  ScanSearch,
  ScanText,
  Atom,
  Table2,
  BookText,
  Search as SearchIcon,
  Workflow,
  LucideIcon,
  MoveRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { ProcessCard } from "./components/ProcessCard";

interface ProcessStep {
  icon: LucideIcon;
  number: number;
  title: string;
  description: string;
}

const HowItWorks: React.FC = () => {
  const processSteps: ProcessStep[] = [
    {
      icon: ScanSearch,
      number: 1,
      title: "Intelligent Extraction",
      description:
        "Our AI algorithms scan and identify areas of interest in your documents, handling diverse layouts from physical books to digitised PDFs. The system automatically identifies tables, chemical structures, text blocks, and other critical elements.",
    },
    {
      icon: ScanText,
      number: 2,
      title: "Advanced Character Recognition",
      description:
        "State-of-the-art OCR models accurately transcribe everything from printed text to difficult handwritten notes. Our models are specifically trained on scientific documents to handle specialized terminology and notation.",
    },
    {
      icon: Atom,
      number: 3,
      title: "Chemical Structure Recognition",
      description:
        "Specialised OCSR models interpret molecular diagrams using various notations and drawing styles. This unlocks the vast amounts of information trapped within chemical structure diagrams.",
    },
    {
      icon: Table2,
      number: 4,
      title: "Table Structuring",
      description:
        "Innovative techniques detect and structure tables, preserving the relationships between data points. Tables are some of the most information-dense elements in scientific documents, and we ensure nothing is lost.",
    },
    {
      icon: BookText,
      number: 5,
      title: "Document Structuring",
      description:
        "Information is brought together across all pages to provide a cohesive understanding of each document. Context is preserved and relationships between different sections are maintained.",
    },
    {
      icon: SearchIcon,
      number: 6,
      title: "Intelligent Search",
      description:
        "Powerful search algorithms make your unique datasets fully searchable. Perform text searches, chemical structure searches, and semantic queries across millions of records to find exactly what you need.",
    },
    {
      icon: Workflow,
      number: 7,
      title: "Seamless Integration",
      description:
        "Export structured data to electronic lab notebooks and other research tools. Our standardised formats ensure compatibility with the workflows you're already using.",
    },
  ];

  return (
    <div className="bg-dark-bg min-h-screen">
      {/* Header */}
      <div className="container mx-auto w-full max-w-5xl px-5 md:px-0 pt-20">
        <h1 className="text-5xl md:text-[3.5rem] leading-none font-semibold">
          <span className="bg-gradient-text bg-clip-text text-transparent">
            How We Revive Your Chemical Data
          </span>
        </h1>
        <p className="mt-4 text-white/60 text-lg max-w-xl leading-relaxed">
          From document scanning to intelligent search — a comprehensive
          AI-powered pipeline built specifically for chemical data.
        </p>
      </div>

      {/* Divider */}
      <div className="container mx-auto w-full max-w-5xl px-5 md:px-0 mt-12">
        <div className="border-t border-white/10" />
      </div>

      {/* Steps */}
      <section className="mt-12 pb-16">
        <div className="container mx-auto w-full max-w-5xl px-5 md:px-0">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-16">
            {/* Left: pipeline steps */}
            <div className="space-y-0">
              {processSteps.map((step, index) => (
                <ProcessCard
                  key={step.number}
                  icon={step.icon}
                  number={step.number}
                  title={step.title}
                  description={step.description}
                  isLast={index === processSteps.length - 1}
                />
              ))}
            </div>

            {/* Right: sticky summary */}
            <div className="hidden md:block">
              <div className="sticky top-24 space-y-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40 mb-3">
                    What you get
                  </p>
                  <ul className="space-y-3">
                    {[
                      "Fully searchable chemical archives",
                      "Structured tables & reaction schemes",
                      "Molecular diagrams as usable SMILES",
                      "Cross-document data relationships",
                      "Export-ready JSON, CSV & ORD formats",
                      "Seamless ELN & tool integrations",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="mt-1.5 w-1 h-1 rounded-full bg-[#00A3FF] shrink-0" />
                        <span className="text-sm text-white/60">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t border-white/10 pt-6">
                  <p className="text-xs uppercase tracking-widest text-white/40 mb-3">
                    Input formats
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "PDF",
                      "Scanned images",
                      "Handwritten notes",
                      "Physical books",
                      "Lab notebooks",
                    ].map((fmt) => (
                      <span
                        key={fmt}
                        className="px-3 py-1 rounded-full border border-white/10 text-white/50 text-xs"
                      >
                        {fmt}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="container mx-auto w-full max-w-5xl px-5 md:px-0">
          <div className="border-t border-white/10 pt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/40 mb-2">
                Get started
              </p>
              <h3 className="text-xl font-semibold text-white">
                Ready to revive your data?
              </h3>
              <p className="text-white/50 text-sm mt-1 max-w-md">
                Join organisations worldwide unlocking the hidden potential in
                their chemical archives.
              </p>
            </div>
            <Link
              to="/pricingPlans"
              className="inline-flex items-center justify-center bg-gradient-to-r from-[#00A3FF] to-[#0077CC] hover:shadow-lg hover:shadow-[#00A3FF]/25 rounded-lg px-6 py-3 text-white text-sm font-semibold transition-all duration-200 shrink-0"
            >
              Get Started <MoveRight className="ml-2" size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
